import * as React from 'react';
import uniqBy from "lodash-es/uniqBy";
import { Link } from "react-router-dom";
import { Authorization } from "../../misc/components/Authorization";
import { HamburgerMenu } from '../../misc/components/HamburgerMenu';
import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { Column, Table } from '../../Components/QueryTable';
import { useQuery, getAnalysisSetsQcBlindingFilter } from '../../Components/Query';
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { DateMatchSortHeader } from '../../Components/DateMatchSortHeader';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { useSecurityContext } from '../../Core/SecurityContext';
import { DlcoAnalysisSetTypeEnum } from '../../misc/api/ApiDlcoAnalysisSetType';
import { DlcoAnalysisSet, WebPortalClient } from "webportalclient";
import { Permission, OverreadStatusIdEnum, User, OverreadChallengeStatusEnum, OverreadStatus } from "../../misc/api/apiTypes";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import Condition from '../../misc/components/Condition';
import { AnalysisSetsTableAutomationId, getAnalysisSetsTableAutomationId, getQueryTableAutomationId, QueryTableAutomationId } from "../../misc/Helpers/AutomationIdHelper";
import { AnalysisSetsOverreadStatusHeader } from "../../Components/AnalysisSetsOverreadStatusHeader";
import { AssignToUserInGroupModal } from '../../misc/components/AssignToUserInGroupModal';
import { AnalysisAcceptabilityStatus } from '../../misc/components/analysis/AnalysisAcceptabilityStatus';
import { CreateAnalysisBatchModal } from '../QC/CreateAnalysisBatchModal';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';
import { SessionChallengeStatus } from '../../misc/components/SessionChallengeStatus';
import { createFormatters } from "../Shared/AnalysisHelpers";

export interface DlcoAnalysisSetListTableRouterProps {
    statusId: string;
}

export interface DlcoAnalysisSetListTableProps {
}

declare const rootUrl: string;

export const DlcoAnalysisSetListTable = (props: DlcoAnalysisSetListTableProps) => {
    const client = useWebPortalApiClient();
    const { study, fieldDisplayText } = useCurrentStudyConfigurationContext();
    const { subjectTypes, overreadStatuses, deviceTypes } = useReferenceContextProvider();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [subjectTypeOptions, setSubjectTypeOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);
    const [serialNumbers, setSerialNumbers] = React.useState([]);
    const [analystSetTypes, setAnalystSetTypes] = React.useState([]);
    const [analysts, setAnalysts] = React.useState([]);
    const [overreadChallengeStatuses, setOverreadChallengeStatuses] = React.useState([]);
    const [sessionIdToReassign, setSessionIdToReassign] = React.useState(null);
    const [isReassignToUserModalVisible, setReassignToUserModalVisible] = React.useState(false);
    const [qcSelectionModalVisible, setQCSelectionModelVisible] = React.useState(false);
    const [qcBatchSaved, setQcBatchSaved] = React.useState<boolean | undefined>(undefined);
    const { data, applyFilters, filters, refresh } = useQuery();
    const legacyClient = useWebPortalLegacyClient();
    const [reassignError, setReassignError] = React.useState<string | undefined>();
    const [assignToUserModalVisible, setAssignToUserModalVisible] = React.useState(false);
    const [sessionIdToSendForQC, setSessionIdToSendForQC] = React.useState(null);

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `study/id eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/study/id eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getSubjectTypes = async () => {

            const subjectsTypes = await client.client.getSubjectTypes("id,name");
            const options = subjectsTypes.value.map(s => ({
                value: s.id,
                label: s.name,
            }));

            setSubjectTypeOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getDlcoSessions("visit", `visit/subject/site/study/id eq ${study.id}`, "visit/name", "visit($select=name)");
            const options = uniqBy(visits.value, o => o.visit.name).map(s => ({
                value: s.visit.name,
                label: s.visit.name,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getDlcoSessions("name", `visit/subject/site/studyId eq ${study.id}`, "name");
            const options = uniqBy(sessions.value, o => o.name).map(s => ({
                value: s.name,
                label: s.name,
            }));

            setSessionOptions(options);
        };

        const getDeviceSerials = async () => {

            const serialNumbers = await client.client.getDlcoSessions("serialNumber", `visit/subject/site/studyId eq ${study.id}`, "serialNumber");
            const options = uniqBy(serialNumbers.value, o => o.serialNumber).map(s => ({
                value: s.serialNumber,
                label: s.serialNumber,
            }));

            setSerialNumbers(options);
        };

        const getAnalystSetTypes = async () => {
            const types = await legacyClient.getDlcoAnalysisSetTypes();
            const options = uniqBy(types, o => o.Id).map(s => ({
                value: s.Id,
                label: s.Name,
            }));

            setAnalystSetTypes(options);
        };

        const getAnalysts = async () => {

            const analysts = await client.client.getDlcoAnalysisSets("user", `session/visit/subject/site/studyId eq ${study.id}`, "user");
            const options = uniqBy(analysts.value, o => o.user).filter(s => s.user !== null && s.user !== undefined).map(s => ({
                value: s.user,
                label: s.user,
            }));

            setAnalysts(options);
        };

        const getOverreadChallengeStatuses = async () => {
            const overreadChallengeStatuses = await client.client.getOverreadChallengeStatuses();

            const options = uniqBy(overreadChallengeStatuses.value, o => o.name).filter(o => o.name).map(s => ({
                value: s.id,
                label: s.name,
            }));

            setOverreadChallengeStatuses(options);
        };

        getSites();
        getSubjects();
        getSubjectTypes();
        getVisits();
        getSessions();
        getDeviceSerials();
        getAnalystSetTypes();
        getAnalysts();
        getOverreadChallengeStatuses();
    }, [study.id]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    if (subjectTypes.length === 0) {
        return (<LoadingWithMessage message="Loading Dlco Sessions..." />);
    }

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }))

    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (analysisSet: DlcoAnalysisSet) => {
        return (<>
            <AnalysisAcceptabilityStatus value={analysisSet.status} />
            <br />
            <SessionChallengeStatus challengeIsClosed={analysisSet.session.challengeIsClosed} challengeStatus={analysisSet.session.overreadChallengeStatus} analysisOverreadStatus={analysisSet.status} />
        </>);
    }

    const formatAnalystSetType = (type: string) => {
        switch (type) {
            case DlcoAnalysisSetTypeEnum.Overread:
                return "Overread";
            case DlcoAnalysisSetTypeEnum.Site:
                return "Site";
            case DlcoAnalysisSetTypeEnum.QC:
                return "QC";
        }
    }

    const handleReassign = async (sessionId: string, userId: string) => {
        try {
            setReassignError(undefined);
            await legacyClient.reassign(sessionId, userId);
            setReassignToUserModalVisible(false);
            refresh();
        } catch (ex) {
            setReassignError(ex.Message);
        }
    }

    const handleSendForQC = async (sessionId: string, userId: string) => {
        await legacyClient.createDlcoQC(sessionId, DlcoAnalysisSetTypeEnum.QC, userId);
        setAssignToUserModalVisible(false);
        refresh();
    }

    const RenderHamburgerMenu = (analysisSet: DlcoAnalysisSet, currentPermissions: Permission[], currentStudyId: string, currentUser: User) => {
        return (
            <HamburgerMenu contextMenuLeftAligned={true} automationId={`options_${analysisSet.id}`} >
                <Authorization permissionId={PermissionEnum.AccessSingleDlcoSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >
                    <Condition test={() => (analysisSet.type !== DlcoAnalysisSetTypeEnum.QC) || (analysisSet.type === DlcoAnalysisSetTypeEnum.QC && currentUser.Username === analysisSet.user)}>
                        <li>
                            <Link data-automationid={`ViewLink_${analysisSet.id}`} to={`/Dlco/Analysis/${analysisSet.id}/view`}>View</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadDlcoSession}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}
                >

                    <Condition test={() =>
                        analysisSet !== null &&
                        analysisSet.type !== DlcoAnalysisSetTypeEnum.Site &&
                        (analysisSet.user === null || analysisSet.user === currentUser.Email) &&
                        (!(analysisSet.user === null && analysisSet.status === OverreadStatusIdEnum.Pending) || analysisSet.user === currentUser.Email)
                    }>
                        <li>
                            <Link data-automationid={`OverreadLink_${analysisSet.id}`} to={`/Dlco/Analysis/${analysisSet.id}/overread`}>Over-read</Link>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.CreateAQCDlcoAnalysisSet}
                    studyId={currentStudyId}
                    currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === DlcoAnalysisSetTypeEnum.Overread}>
                        <li>
                            <a onClick={() => { setAssignToUserModalVisible(true); setSessionIdToSendForQC(analysisSet.sessionId); }}>
                                Manually Send for QC Review...
                            </a>
                        </li>
                    </Condition>
                </Authorization>
                <Authorization permissionId={PermissionEnum.OverreadDlcoSession} studyId={currentStudyId} currentPermissions={currentPermissions}>
                    <Condition test={() => analysisSet.type === DlcoAnalysisSetTypeEnum.Overread
                        && (analysisSet.status === OverreadStatusIdEnum.Pending || analysisSet.status === OverreadStatusIdEnum.NotStarted)
                    }>
                        <li>
                            <a onClick={() => { setSessionIdToReassign(analysisSet.id); setReassignToUserModalVisible(true); }}>Reassign</a>
                        </li>
                    </Condition>
                </Authorization>
            </HamburgerMenu>
        );
    };

    const handleQcBatchSavingAlertMessage = () => {
        if (qcBatchSaved) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-success">
                        <i className="fa fa-check icon"></i>
                        QC batch is created successfully.
                    </div>
                </div>
            </div>);
        }
        else if (qcBatchSaved === false) {
            return (<div className="row">
                <div className="col-md-12">
                    <div className="alert alert-danger">
                        <i className="fa fa-times icon"></i>
                        There was an error. QC batch wasn't saved.
                    </div>
                </div>
            </div>);
        }
        else {
            return null
        }
    }

    const isQcAnalysisSet = (set: DlcoAnalysisSet): boolean => set.type === DlcoAnalysisSetTypeEnum.QC;

    const dlcoFormatters = createFormatters<DlcoAnalysisSet>(isQcAnalysisSet);

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Dlco Analysis Sets</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters} data-automationid={`${getQueryTableAutomationId(QueryTableAutomationId.ClearAllFilters)}`}>
                            Clear All Filters
                        </button>&nbsp;
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.CreateAutomatedQCDlcoSelection}>
                            <button className="btn btn-primary" onClick={() => setQCSelectionModelVisible(true)}>Create QC Batch</button>
                        </Authorization>
                    </div>
                </div>
            </div>

            {handleQcBatchSavingAlertMessage()}

            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div id="AnalysisSetsTable" className="table-responsive" data-automationid="AnalysisSetsTable" style={{ minHeight: "900px" }}>
                                <Table data={data} automationId="DlcoAnalysisListTable">
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SiteNumber)}`} header={<MatchSortHeader title="Site ID" field="session/visit/subject/site/id" options={siteOptions} sortField="session/visit/subject/site/number" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SiteNumber)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => dlcoFormatters.formatSiteId(set)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectNumber)}`} header={<MatchSortHeader title={fieldDisplayText.SubjectId} field="session/visit/subject/id" options={subjectOptions} sortField="session/visit/subject/number" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectNumber)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => dlcoFormatters.formatSubjectId(set)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectType)}`} header={<MatchSortHeader title={fieldDisplayText.SubjectType} field="session/visit/subject/typeKey" options={subjectTypeOptions} sortField="session/visit/subject/typeName" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SubjectType)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => dlcoFormatters.formatSubjectType(set)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.VisitName)}`} header={<MatchSortHeader title="Visit" field="session/visit/name" options={visitOptions} sortField="session/visit/name" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.VisitName)}`} relatedFilter={getAnalysisSetsQcBlindingFilter()} />} formatter={set => dlcoFormatters.formatVisitLabel(set)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SessionLabel)}`} header={<MatchSortHeader title="Session" field="session/name" options={sessionOptions} sortField="session/name" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.SessionLabel)}`} />} formatter={set => dlcoFormatters.formatSessionLabel(set)} />
                                    <Column field="type" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.AnalysisSetType)}`} header={<MatchSortHeader title="Analysis Set Type" field="type" options={analystSetTypes} automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.AnalysisSetType)}`} />} formatter={type => formatAnalystSetType(type)} />
                                    <Column field="created" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.CreatedAt)}`} header={<DateMatchSortHeader title="Created At" field="created" sortField="created" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.CreatedAt)}`} />} formatter={created => formatDateTime(created)} />
                                    <Column field="started" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.StartedAt)}`} header={<DateMatchSortHeader title="Started At" field="started" sortField="started" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.StartedAt)}`} />} formatter={started => formatDateTime(started)} />
                                    <Column field="" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Status)}`}
                                        header={<AnalysisSetsOverreadStatusHeader
                                            title="Status"
                                            sortField="status"
                                            automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Status)}`}
                                            overreadStatuses={statusesOptions}
                                            overreadChallengeStatuses={overreadChallengeStatuses}
                                        />}
                                        formatter={(analysisSet: DlcoAnalysisSet) => formatStatus(analysisSet)}
                                    />
                                    {ViewOverreaderName()
                                        ? <Column field="user" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Overreader)}`} header={<MatchSortHeader title="Over-reader" field="user" options={analysts} sortField="user" automationId={`${getAnalysisSetsTableAutomationId(AnalysisSetsTableAutomationId.Overreader)}`} />} formatter={analyst => formatAnalyst(analyst)} />
                                        : null
                                    }
                                    <Column field="" formatter={(analysisSet: DlcoAnalysisSet) => RenderHamburgerMenu(analysisSet, currentUserPermissions, study.id, currentUser)} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <AssignToUserInGroupModal show={isReassignToUserModalVisible}
                headerText="Reassign Analysis Set"
                error={reassignError}
                permission={PermissionEnum.OverreadDlcoSession}
                onClose={() => setReassignToUserModalVisible(false)}
                onAssign={(user) => { handleReassign(sessionIdToReassign, user) }} />

            <AssignToUserInGroupModal show={assignToUserModalVisible} headerText="Create QC Analysis Set &amp; assign to user" permission={PermissionEnum.OverreadDlcoSession}
                onClose={() => setAssignToUserModalVisible(false)}
                onAssign={(user) => handleSendForQC(sessionIdToSendForQC, user)} />

            {qcSelectionModalVisible ? <CreateAnalysisBatchModal currentUser={currentUser} sessionType={SessionTypes.Dlco} minimumTests={2} onClose={() => setQCSelectionModelVisible(false)} onSave={(result: boolean) => setQcBatchSaved(result)} /> : null}
        </>
    );

}
